import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getHealthSummary, getTicketSummary, getUnreadCount } from "../api/support.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface TicketSummary {
  open: number;
  in_progress: number;
  resolved: number;
  overdue: number;
}
interface HealthSummary {
  healthy: number;
  at_risk: number;
  churned: number;
}

export function SupportOverviewPage() {
  const { token } = useAuth();
  const [tickets, setTickets] = useState<TicketSummary | null>(null);
  const [health, setHealth] = useState<HealthSummary | null>(null);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void Promise.all([
      getTicketSummary(token),
      getHealthSummary(token),
      getUnreadCount(token),
    ]).then(([t, h, u]) => {
      setTickets(t as TicketSummary);
      setHealth(h as HealthSummary);
      setUnread((u as { count: number }).count ?? 0);
      setLoading(false);
    });
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Customer Support & Communications</h1>
          <p className="mt-1 text-gray-400">
            Phase 9 — Tickets, Notifications, Meetings, SLA & Customer Health
          </p>
        </div>
        {!loading && (
          <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {tickets && (
              <>
                <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <p className="text-xs text-gray-400">Open Tickets</p>
                  <p className="text-2xl font-bold text-yellow-400">{tickets.open}</p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <p className="text-xs text-gray-400">In Progress</p>
                  <p className="text-2xl font-bold text-blue-400">{tickets.in_progress}</p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <p className="text-xs text-gray-400">Overdue</p>
                  <p
                    className={`text-2xl font-bold ${tickets.overdue > 0 ? "text-red-400" : "text-gray-400"}`}
                  >
                    {tickets.overdue}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <p className="text-xs text-gray-400">Unread Notifications</p>
                  <p
                    className={`text-2xl font-bold ${unread > 0 ? "text-indigo-400" : "text-gray-400"}`}
                  >
                    {unread}
                  </p>
                </div>
              </>
            )}
            {health && (
              <>
                <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <p className="text-xs text-gray-400">Healthy Clients</p>
                  <p className="text-2xl font-bold text-green-400">{health.healthy}</p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <p className="text-xs text-gray-400">At Risk</p>
                  <p
                    className={`text-2xl font-bold ${health.at_risk > 0 ? "text-yellow-400" : "text-gray-400"}`}
                  >
                    {health.at_risk}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <p className="text-xs text-gray-400">Churned</p>
                  <p
                    className={`text-2xl font-bold ${health.churned > 0 ? "text-red-400" : "text-gray-400"}`}
                  >
                    {health.churned}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <p className="text-xs text-gray-400">Resolved Tickets</p>
                  <p className="text-2xl font-bold text-green-400">{tickets?.resolved ?? 0}</p>
                </div>
              </>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { to: "/support/tickets", label: "Tickets", desc: "Track and resolve customer issues" },
            {
              to: "/support/notifications",
              label: "Notifications",
              desc: "In-app and email notification feed",
            },
            {
              to: "/support/meetings",
              label: "Meeting Notes",
              desc: "Record summaries and action items",
            },
            { to: "/support/sla", label: "SLA Dashboard", desc: "Response & resolution targets" },
            {
              to: "/support/health",
              label: "Customer Health",
              desc: "Health scores and churn risk",
            },
            {
              to: "/support/activity",
              label: "Activity Feed",
              desc: "All support events in real time",
            },
          ].map((nav) => (
            <Link
              key={nav.to}
              to={nav.to}
              className="block rounded-lg border border-gray-800 bg-gray-900 p-4 transition-colors hover:border-indigo-500"
            >
              <p className="font-medium text-white">{nav.label}</p>
              <p className="mt-1 text-sm text-gray-400">{nav.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
